import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { ApiCall } from "../helper/axios";
// ==========================================================================
// ✅ ASYNC THUNKS
// ==========================================================================
// Fetch Divergence Report
export const fetchDivergenceReport = createAsyncThunk("divergence/fetchReport", async (payload, { getState, rejectWithValue }) => {
    try {
        const { auth } = getState();
        const token = auth?.token;
        const response = await ApiCall("POST", "/api/divergence-report", payload, {
            Authorization: token || ""
        });
        if (response && 'data' in response && response?.data) {
            const responseData = response.data?.data;
            const finalData = {
                available_metrics: responseData?.available_metrics || [],
                last_updated: responseData?.last_updated || null,
                results: responseData?.results || [],
                cache: responseData?.cache || null
            };
            return finalData;
        }
        else {
            return rejectWithValue("Failed to fetch divergence report");
        }
    }
    catch (error) {
        console.error("Divergence Report API Error:", error);
        return rejectWithValue(error?.response?.data?.message ||
            error.message ||
            "An error occurred while fetching divergence report");
    }
});
// Fetch Chart Data
export const fetchChartData = createAsyncThunk("divergence/fetchChartData", async (payload, { getState, rejectWithValue }) => {
    try {
        const { auth } = getState();
        const token = auth?.token;
        const response = await ApiCall("POST", "/api/divergence-chart-data", payload, {
            Authorization: token || ""
        });
        if (response && 'data' in response && response?.data) {
            const responseData = response.data?.data;
            return responseData;
        }
        else {
            return rejectWithValue("Failed to fetch chart data");
        }
    }
    catch (error) {
        console.error("Chart Data API Error:", error);
        return rejectWithValue(error?.response?.data?.message ||
            error.message ||
            "An error occurred while fetching chart data");
    }
});
// ✅ Fetch Preferences (no payload needed - modulekey from header or explicit payload)
export const fetchPreferences = createAsyncThunk("divergence/fetchPreferences", async (payload, { getState, rejectWithValue }) => {
    try {
        const { auth } = getState();
        const token = auth?.token;
        const headers = {
            Authorization: token || ""
        };
        if (payload && payload.moduleKey) {
            headers['x-module-key'] = payload.moduleKey;
        }
        const response = await ApiCall("GET", `/api/preferences`, null, headers);
        if (response && 'data' in response && response?.data) {
            const responseData = response.data;
            return responseData?.data || {};
        }
        else {
            return rejectWithValue("Failed to fetch preferences");
        }
    }
    catch (error) {
        console.error("Fetch Preferences API Error:", error);
        return rejectWithValue(error?.response?.data?.message ||
            error.message ||
            "An error occurred while fetching preferences");
    }
});
// ✅ Save Preferences (only preferences in payload - modulekey from header or explicit payload)
export const savePreferences = createAsyncThunk("divergence/savePreferences", async (payload, { getState, rejectWithValue }) => {
    try {
        const { auth } = getState();
        const token = auth?.token;
        const headers = {
            Authorization: token || ""
        };
        if (payload && payload.moduleKey) {
            headers['x-module-key'] = payload.moduleKey;
        }
        const response = await ApiCall("POST", "/api/preferences", {
            preferences: payload.preferences
        }, headers);
        if (response && 'data' in response && response?.data) {
            const responseData = response.data;
            return responseData?.data;
        }
        else {
            return rejectWithValue("Failed to save preferences");
        }
    }
    catch (error) {
        console.error("Save Preferences API Error:", error);
        return rejectWithValue(error?.response?.data?.message ||
            error.message ||
            "An error occurred while saving preferences");
    }
});
// Fetch Divergence Change Logs
export const fetchDivergenceChangeLogs = createAsyncThunk("divergence/fetchChangeLogs", async (payload, { getState, rejectWithValue }) => {
    try {
        const { auth } = getState();
        const token = auth?.token;
        const response = await ApiCall("POST", "/api/divergence-change-logs", { clientId: payload.clientId }, {
            Authorization: token || ""
        });
        if (response && 'data' in response && response?.data) {
            const responseData = response.data?.data;
            return responseData || [];
        }
        else {
            return rejectWithValue("Failed to fetch change logs");
        }
    }
    catch (error) {
        console.error("Fetch Change Logs API Error:", error);
        return rejectWithValue(error?.response?.data?.message ||
            error.message ||
            "An error occurred while fetching change logs");
    }
});
// ==========================================================================
// ✅ INITIAL STATE
// ==========================================================================
const initialState = {
    reportData: null,
    reportLoading: false,
    reportError: null,
    chartData: null,
    chartLoading: false,
    chartError: null,
    availableMetrics: [],
    lastUpdated: null,
    // ✅ Preferences initial state
    preferences: null,
    preferencesLoading: false,
    preferencesError: null,
    preferencesSaving: false,
    preferencesSaveError: null,
    changeLogs: [],
    changeLogsLoading: false,
    changeLogsError: null,
};
// ==========================================================================
// ✅ SLICE
// ==========================================================================
const divergenceSlice = createSlice({
    name: "divergence",
    initialState,
    reducers: {
        // Clear Report Data
        clearReportData: (state) => {
            state.reportData = null;
            state.reportError = null;
        },
        // Clear Report Error
        clearReportError: (state) => {
            state.reportError = null;
        },
        // Clear Chart Data
        clearChartData: (state) => {
            state.chartData = null;
            state.chartError = null;
        },
        // Clear Chart Error
        clearChartError: (state) => {
            state.chartError = null;
        },
        // Clear All Divergence Data
        clearAllDivergenceData: (state) => {
            state.reportData = null;
            state.reportError = null;
            state.chartData = null;
            state.chartError = null;
            state.availableMetrics = [];
            state.lastUpdated = null;
            state.preferences = null;
            state.preferencesError = null;
            state.preferencesSaveError = null;
        },
        // Set Available Metrics
        setAvailableMetrics: (state, action) => {
            state.availableMetrics = action.payload;
        },
        // Set Last Updated
        setLastUpdated: (state, action) => {
            state.lastUpdated = action.payload;
        },
        // Clear Last Updated
        clearLastUpdated: (state) => {
            state.lastUpdated = null;
        },
        // ✅ Set Preferences (local update)
        setPreferences: (state, action) => {
            state.preferences = action.payload;
        },
        // ✅ Clear Preferences
        clearPreferences: (state) => {
            state.preferences = null;
            state.preferencesError = null;
            state.preferencesSaveError = null;
        },
        // ✅ Clear Preferences Error
        clearPreferencesError: (state) => {
            state.preferencesError = null;
        },
        // ✅ Clear Preferences Save Error
        clearPreferencesSaveError: (state) => {
            state.preferencesSaveError = null;
        },
        // ✅ Update Single Network Preference (local update before saving)
        updateNetworkPreference: (state, action) => {
            const { networkKey, metrics } = action.payload;
            if (!state.preferences) {
                state.preferences = {};
            }
            state.preferences[networkKey] = metrics;
        },
        // ✅ Update Single Metric in Network (local update)
        updateMetricPreference: (state, action) => {
            const { networkKey, metricKey, preference } = action.payload;
            if (!state.preferences) {
                state.preferences = {};
            }
            if (!state.preferences[networkKey]) {
                state.preferences[networkKey] = [];
            }
            // Find and update existing metric or add new one
            const existingIndex = state.preferences[networkKey].findIndex(item => Object.keys(item)[0] === metricKey);
            if (existingIndex !== -1) {
                state.preferences[networkKey][existingIndex] = { [metricKey]: preference };
            }
            else {
                state.preferences[networkKey].push({ [metricKey]: preference });
            }
        },
        // ✅ Toggle Metric Visibility (local update)
        toggleMetricVisibility: (state, action) => {
            const { networkKey, metricKey } = action.payload;
            if (!state.preferences?.[networkKey])
                return;
            const metricIndex = state.preferences[networkKey].findIndex(item => Object.keys(item)[0] === metricKey);
            if (metricIndex !== -1) {
                const currentVisibility = state.preferences[networkKey][metricIndex][metricKey].visibility;
                state.preferences[networkKey][metricIndex][metricKey].visibility = !currentVisibility;
            }
        },
        // ✅ Update Metric Order (local update)
        updateMetricOrder: (state, action) => {
            const { networkKey, metricKey, order } = action.payload;
            if (!state.preferences?.[networkKey])
                return;
            const metricIndex = state.preferences[networkKey].findIndex(item => Object.keys(item)[0] === metricKey);
            if (metricIndex !== -1) {
                state.preferences[networkKey][metricIndex][metricKey].order = order;
            }
        },
    },
    extraReducers: (builder) => {
        // ========== DIVERGENCE REPORT ==========
        builder
            .addCase(fetchDivergenceReport.pending, (state) => {
            state.reportLoading = true;
            state.reportError = null;
        })
            .addCase(fetchDivergenceReport.fulfilled, (state, action) => {
            state.reportLoading = false;
            state.reportData = action.payload;
            state.reportError = null;
            if (action.payload?.available_metrics) {
                state.availableMetrics = action.payload.available_metrics;
            }
            if (action.payload?.last_updated) {
                state.lastUpdated = action.payload.last_updated;
            }
        })
            .addCase(fetchDivergenceReport.rejected, (state, action) => {
            state.reportLoading = false;
            state.reportError = action.payload || "Failed to fetch report";
        });
        // ========== CHART DATA ==========
        builder
            .addCase(fetchChartData.pending, (state) => {
            state.chartLoading = true;
            state.chartError = null;
        })
            .addCase(fetchChartData.fulfilled, (state, action) => {
            state.chartLoading = false;
            state.chartData = action.payload;
            state.chartError = null;
        })
            .addCase(fetchChartData.rejected, (state, action) => {
            state.chartLoading = false;
            state.chartError = action.payload || "Failed to fetch chart data";
        });
        // ========== FETCH PREFERENCES ==========
        builder
            .addCase(fetchPreferences.pending, (state) => {
            state.preferencesLoading = true;
            state.preferencesError = null;
        })
            .addCase(fetchPreferences.fulfilled, (state, action) => {
            state.preferencesLoading = false;
            state.preferences = action.payload;
            state.preferencesError = null;
        })
            .addCase(fetchPreferences.rejected, (state, action) => {
            state.preferencesLoading = false;
            state.preferencesError = action.payload || "Failed to fetch preferences";
        });
        // ========== SAVE PREFERENCES ==========
        builder
            .addCase(savePreferences.pending, (state) => {
            state.preferencesSaving = true;
            state.preferencesSaveError = null;
        })
            .addCase(savePreferences.fulfilled, (state, action) => {
            state.preferencesSaving = false;
            state.preferencesSaveError = null;
            // Update preferences with response data
            if (action.payload?.preferences) {
                state.preferences = action.payload.preferences;
            }
        })
            .addCase(savePreferences.rejected, (state, action) => {
            state.preferencesSaving = false;
            state.preferencesSaveError = action.payload || "Failed to save preferences";
        });
        // ========== CHANGE LOGS ==========
        builder
            .addCase(fetchDivergenceChangeLogs.pending, (state) => {
            state.changeLogsLoading = true;
            state.changeLogsError = null;
        })
            .addCase(fetchDivergenceChangeLogs.fulfilled, (state, action) => {
            state.changeLogsLoading = false;
            state.changeLogs = action.payload;
            state.changeLogsError = null;
        })
            .addCase(fetchDivergenceChangeLogs.rejected, (state, action) => {
            state.changeLogsLoading = false;
            state.changeLogsError = action.payload || "Failed to fetch change logs";
        });
    },
});
// ==========================================================================
// ✅ EXPORTS
// ==========================================================================
export const { clearReportData, clearReportError, clearChartData, clearChartError, clearAllDivergenceData, setAvailableMetrics, setLastUpdated, clearLastUpdated, 
// ✅ Preferences actions
setPreferences, clearPreferences, clearPreferencesError, clearPreferencesSaveError, updateNetworkPreference, updateMetricPreference, toggleMetricVisibility, updateMetricOrder, } = divergenceSlice.actions;
// ==========================================================================
// ✅ SELECTORS
// ==========================================================================
// Report Selectors
export const selectReportData = (state) => state.divergence.reportData;
export const selectReportLoading = (state) => state.divergence.reportLoading;
export const selectReportError = (state) => state.divergence.reportError;
// Chart Selectors
export const selectChartData = (state) => state.divergence.chartData;
export const selectChartLoading = (state) => state.divergence.chartLoading;
export const selectChartError = (state) => state.divergence.chartError;
// Available Metrics Selector
export const selectAvailableMetrics = (state) => state.divergence.availableMetrics;
// Last Updated Selectors
export const selectLastUpdated = (state) => state.divergence.lastUpdated;
export const selectFormattedLastUpdated = (state) => {
    const lastUpdated = state.divergence.lastUpdated;
    if (!lastUpdated)
        return null;
    const date = new Date(lastUpdated);
    return date.toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
    });
};
// ✅ Preferences Selectors
export const selectPreferences = (state) => state.divergence.preferences;
export const selectPreferencesLoading = (state) => state.divergence.preferencesLoading;
export const selectPreferencesError = (state) => state.divergence.preferencesError;
export const selectPreferencesSaving = (state) => state.divergence.preferencesSaving;
export const selectPreferencesSaveError = (state) => state.divergence.preferencesSaveError;
// ✅ Get Network Preferences
export const selectNetworkPreferences = (networkKey) => (state) => {
    return state.divergence.preferences?.[networkKey] || [];
};
// ✅ Get Metric Preference
export const selectMetricPreference = (networkKey, metricKey) => (state) => {
    const networkPrefs = state.divergence.preferences?.[networkKey] || [];
    const metricPref = networkPrefs.find(item => Object.keys(item)[0] === metricKey);
    return metricPref ? metricPref[metricKey] : null;
};
// ✅ Check if Metric is Visible
export const selectIsMetricVisible = (networkKey, metricKey) => (state) => {
    const networkPrefs = state.divergence.preferences?.[networkKey] || [];
    const metricPref = networkPrefs.find(item => Object.keys(item)[0] === metricKey);
    return metricPref ? metricPref[metricKey]?.visibility !== false : true;
};
// ✅ Get Ordered Metrics for Network
export const selectOrderedMetrics = (networkKey) => (state) => {
    const networkPrefs = state.divergence.preferences?.[networkKey] || [];
    return [...networkPrefs]
        .filter(item => {
        const metricKey = Object.keys(item)[0];
        return item[metricKey]?.visibility !== false;
    })
        .sort((a, b) => {
        const keyA = Object.keys(a)[0];
        const keyB = Object.keys(b)[0];
        const orderA = a[keyA]?.order ?? 999;
        const orderB = b[keyB]?.order ?? 999;
        return orderA - orderB;
    })
        .map(item => Object.keys(item)[0]);
};
// Change Logs Selectors
export const selectChangeLogs = (state) => state.divergence.changeLogs;
export const selectChangeLogsLoading = (state) => state.divergence.changeLogsLoading;
export const selectChangeLogsError = (state) => state.divergence.changeLogsError;
export default divergenceSlice.reducer;
